import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ReactSVG } from 'react-svg';
import { useSpring, animated } from '@react-spring/web';
import song_fallback from '../../assets/song_fallback.svg';
import record_player_arm from '../../assets/record_player_arm.svg';
import spirals from '../../assets/backgrounds/spirals.svg';

import '../Slides.scss'
import './SongSlide.scss';

const SongSlide = ({ data }) => {

  const { song_asset } = data;
  const [imgSrc, setImgSrc] = useState(song_asset || song_fallback);

  const titleSpring = useSpring({
    from: { opacity: 0, transform: 'translateX(100px)' },
    to: { opacity: 1, transform: 'translateX(0px)' },
    config: { tension: 180, friction: 12 },
    delay: 200,
  });

  const fadeDuration = 1000;

  const fadeSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { duration: fadeDuration },
  });

  const [style, api] = useSpring(() => ({
    transform: 'rotate(0deg)',
    config: { duration: 10000 },
  }));

  useEffect(() => {
    let isCancelled = false;

    const startCycle = () => {
      if (isCancelled) return;

      api.start({
        from: { transform: 'rotate(0deg)' },
        to: async (next) => {
          // Fast rotation for ~270 degrees
          await next({
            transform: 'rotate(270deg)',
            config: { duration: 7000 },
          });

          // Slow down near 359 degrees
          await next({
            transform: 'rotate(359deg)',
            config: { duration: 2000 },
          });

          // Pause for 1 second at 359 degrees
          await next({
            transform: 'rotate(359deg)',
            config: { duration: 1000 },
          });
        },
        onRest: () => {
          // Restart the animation after completion
          if (!isCancelled) {
            startCycle();
          }
        },
        delay: fadeDuration,
      });
    };

    startCycle();

    return () => {
      isCancelled = true;
    };
  }, [api]);

  const assetSpring = {
    ...style,
    opacity: fadeSpring.opacity,
  };

  const boxSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(100vh)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 600,
  });

  const spiralSpring = useSpring({
    from: { transform: 'scale(1)' },
    to: { transform: 'scale(1.1)' },
    loop: { reverse: true },
    config: { duration: 6000, easing: (t) => t * (2 - t) }, // Smooth easing and slower animation
    delay: 400,
  })
  return (
    <div className='Slide__song'>
      <animated.h1 className='Slide__song__title' style={titleSpring}>
        {imgSrc !== song_fallback ? 'The band is learning' : 'Song coming soon...'}
      </animated.h1>
      <animated.div className='Slide__song__asset'>
        <animated.img
          className='Slide__song__record'
          style={assetSpring}
          src={imgSrc}
          onError={() => setImgSrc(song_fallback)}
        />
        <animated.div className='Slide__song__arm'>
          <ReactSVG src={record_player_arm} />
        </animated.div>
      </animated.div>
      <animated.div className='Slide__song__box' style={boxSpring} />
      <animated.div className='Slide__song__spirals' style={spiralSpring}>
        <ReactSVG src={spirals} />
      </animated.div>
    </div>
  );
}

export default SongSlide;

const DataProps = {
  song_asset: PropTypes.string,
};

SongSlide.propTypes = {
  data: PropTypes.shape(DataProps).isRequired,
};
